import { useEffect } from "react";
import LegalLayout, { H2 } from "./LegalLayout";

/**
 * Política de privacidad — customer-facing.
 * Cubre: qué datos recogemos, cómo se usan, consentimiento de SMS (10DLC / STOP-HELP),
 * píxeles/publicidad (Meta), terceros, retención, derechos y contacto.
 * Requerida por Meta para correr anuncios y por los carriers para A2P.
 */
export default function Privacy() {
  useEffect(() => {
    document.title = "Privacy Policy · A Couple & A Dream";
  }, []);

  return (
    <LegalLayout title="Privacy Policy" updated="July 16, 2026">
      <p>
        This Privacy Policy explains how A Couple &amp; A Dream, a brand of Javana
        Solutions LLC (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;),
        collects, uses, and protects your information when you use our website and
        request a design consultation for a bathroom transformation.
      </p>

      <section className="space-y-3">
        <H2>Information we collect</H2>
        <p>When you complete a form on our site, we may collect:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>Your name, phone number, and email address;</li>
          <li>Your home address and ZIP code (to confirm we serve your area);</li>
          <li>
            Project details you share with us, such as the room, scope of work, and
            style preferences you select in our project builder;
          </li>
          <li>
            Technical data such as your IP address, browser type, and pages viewed,
            collected automatically through cookies and similar technologies.
          </li>
        </ul>
      </section>

      <section className="space-y-3">
        <H2>How we use your information</H2>
        <ul className="list-disc space-y-1 pl-5">
          <li>To respond to your request and schedule an in-home design consultation;</li>
          <li>To contact you by phone call, text message, and email about your project;</li>
          <li>To prepare a tentative investment range and project proposal;</li>
          <li>To improve our website, services, and advertising;</li>
          <li>To comply with legal obligations.</li>
        </ul>
      </section>

      <section className="space-y-3">
        <H2>SMS / text messaging</H2>
        <p>
          By providing your phone number and agreeing to be contacted, you consent to
          receive calls and text messages from A Couple &amp; A Dream related to your
          bathroom project, including appointment scheduling and follow-up. Consent to
          receive text messages is not a condition of any purchase.
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>Message frequency varies based on your interaction with us.</li>
          <li>Message and data rates may apply.</li>
          <li>Reply <strong>STOP</strong> at any time to opt out of text messages.</li>
          <li>Reply <strong>HELP</strong> for assistance, or email us at hello@acouplenadream.com.</li>
        </ul>
        <p>
          Mobile opt-in data and consent are never shared with third parties or
          affiliates for their own marketing purposes.
        </p>
      </section>

      <section className="space-y-3">
        <H2>Advertising &amp; analytics</H2>
        <p>
          We use the Meta Pixel and Conversions API to measure the performance of our
          advertising and to show relevant ads on platforms such as Facebook and
          Instagram. When we share event data with Meta for measurement, contact
          details are hashed (irreversibly encrypted) before transmission. You can
          manage ad personalization in your Facebook and Instagram settings.
        </p>
      </section>

      <section className="space-y-3">
        <H2>How we share information</H2>
        <p>
          We do not sell your personal information. We may share it with trusted service
          providers who help us operate our business — such as our scheduling and CRM
          platform, email delivery provider, and advertising platforms — solely to
          provide services to you. These providers are bound by confidentiality
          obligations.
        </p>
      </section>

      <section className="space-y-3">
        <H2>Data retention</H2>
        <p>
          We keep your information for as long as needed to serve you and to meet our
          legal, tax, and accounting requirements. You may request deletion of your
          information at any time (see below).
        </p>
      </section>

      <section className="space-y-3">
        <H2>Your choices</H2>
        <p>
          You may request to access, correct, or delete your personal information, or
          opt out of marketing communications, by emailing{" "}
          <a className="underline hover:text-clay" href="mailto:hello@acouplenadream.com">
            hello@acouplenadream.com
          </a>
          . You can unsubscribe from emails using the link in any message, and stop
          texts by replying STOP.
        </p>
      </section>

      <section className="space-y-3">
        <H2>Contact us</H2>
        <p>
          A Couple &amp; A Dream (a brand of Javana Solutions LLC)
          <br />
          Tampa Bay, Florida
          <br />
          <a className="underline hover:text-clay" href="mailto:hello@acouplenadream.com">
            hello@acouplenadream.com
          </a>
        </p>
      </section>
    </LegalLayout>
  );
}
